import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const { getAuthHeader } = useAuth();
  const [status, setStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    const checkPaymentStatus = async () => {
      if (!sessionId || attempts >= 5) {
        if (attempts >= 5) setStatus('timeout');
        return;
      }

      try {
        const response = await axios.get(
          `${API}/payments/status/${sessionId}`,
          getAuthHeader()
        );

        if (response.data.payment_status === 'paid') {
          setStatus('success');
        } else if (response.data.status === 'expired') {
          setStatus('expired');
        } else {
          // Continue polling
          setAttempts(prev => prev + 1);
          setTimeout(checkPaymentStatus, 2000);
        }
      } catch (error) {
        console.error('Payment status error:', error);
        setAttempts(prev => prev + 1);
        setTimeout(checkPaymentStatus, 2000);
      }
    };

    checkPaymentStatus();
  }, [sessionId, attempts]);

  if (status === 'checking') {
    return (
      <div className="min-h-[70vh] flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <Loader2 className="h-12 w-12 text-[#C5A059] animate-spin mx-auto mb-4" />
          <h2 className="font-['Playfair_Display'] text-2xl font-bold text-[#0E285B] mb-2">
            Processing Payment...
          </h2>
          <p className="text-slate-600">Please wait while we confirm your payment.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[70vh] flex items-center justify-center bg-slate-50">
      <div className="max-w-md w-full mx-auto p-8 bg-white rounded-sm shadow-lg text-center" data-testid="payment-success">
        <div className="h-20 w-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="h-10 w-10 text-green-600" />
        </div>
        <h2 className="font-['Playfair_Display'] text-2xl font-bold text-[#0E285B] mb-4">
          Payment Successful!
        </h2>
        <p className="text-slate-600 mb-6">
          Thank you for your payment. Your membership is now active. 
          You can access your certificate from your member dashboard.
        </p>
        <div className="flex flex-col gap-3">
          <Link to="/member">
            <Button 
              className="w-full bg-[#0E285B] text-white hover:bg-[#0E285B]/90"
              data-testid="go-to-dashboard-btn"
            >
              Go to Dashboard
            </Button>
          </Link>
          <Link to="/">
            <Button variant="outline" className="w-full">
              Back to Home
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
